import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { UserPlus, Users } from 'lucide-react';

import type { Member, User } from '@tasks/contracts';

import { addMember, listMembers, removeMember, updateMemberRole } from '../../api/projects';
import { queryKeys } from '../../api/queryKeys';
import { listUsers } from '../../api/users';
import { Avatar } from '../../components/Avatar';
import { Button } from '../../components/Button';
import { Combobox } from '../../components/Combobox';
import { EmptyState } from '../../components/EmptyState';
import { IconButton } from '../../components/IconButton';
import { Modal } from '../../components/Modal';
import { Select, type SelectOption } from '../../components/Select';
import { Skeleton } from '../../components/Skeleton';
import { Table, type TableColumn } from '../../components/Table';
import { useToast } from '../../components/Toast';
import { useAuth } from '../../auth/AuthProvider';
import { humanizeError } from '../../lib/errors';
import { formatDate } from '../../lib/format';
import { projectRoleLabels } from '../../i18n/enums';
import { cn } from '../../lib/cn';
import type { ProjectRole } from '../../lib/types';
import { Trash2 } from 'lucide-react';
import styles from './SettingsPages.module.css';

const ROLE_OPTIONS: readonly SelectOption<ProjectRole>[] = (['project_admin', 'member'] as const).map((value) => ({
  value,
  label: projectRoleLabels[value],
}));

/** Участники проекта: добавление, смена роли, удаление с переназначением (UC-PRJ-03/04). */
export function MembersSettingsPage() {
  const { key = '' } = useParams<{ key: string }>();
  const queryClient = useQueryClient();
  const toast = useToast();
  const { user } = useAuth();

  const [isAddOpen, setAddOpen] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState<readonly string[]>([]);
  const [newRole, setNewRole] = useState<ProjectRole>('member');
  const [memberToRemove, setMemberToRemove] = useState<Member | null>(null);
  const [reassign, setReassign] = useState<'keep' | 'null'>('keep');

  const membersQuery = useQuery({
    queryKey: queryKeys.projects.members(key),
    queryFn: ({ signal }) => listMembers(key, signal),
    enabled: key.length > 0,
  });

  const usersQuery = useQuery({
    queryKey: queryKeys.admin.users('', 'active', 0),
    queryFn: ({ signal }) => listUsers({ status: 'active', limit: 100, offset: 0 }, signal),
    enabled: isAddOpen,
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.projects.members(key) });
    void queryClient.invalidateQueries({ queryKey: queryKeys.projects.detail(key) });
  };

  const addMutation = useMutation({
    mutationFn: async () => {
      for (const userId of selectedUserIds) {
        await addMember(key, { userId, role: newRole });
      }
    },
    onSuccess: () => {
      setAddOpen(false);
      setSelectedUserIds([]);
      setNewRole('member');
      invalidate();
      toast.success('Участники добавлены');
    },
    onError: (cause: unknown) => {
      const human = humanizeError(cause);
      toast.error(human.message, { code: human.code });
    },
  });

  const roleMutation = useMutation({
    mutationFn: (variables: { readonly userId: string; readonly role: ProjectRole }) =>
      updateMemberRole(key, variables.userId, variables.role),
    onSuccess: invalidate,
    onError: (cause: unknown) => {
      const human = humanizeError(cause);
      toast.error(human.message, { code: human.code });
    },
  });

  const removeMutation = useMutation({
    mutationFn: (userId: string) => removeMember(key, userId, reassign),
    onSuccess: () => {
      setMemberToRemove(null);
      invalidate();
      toast.success('Участник удалён');
    },
    onError: (cause: unknown) => {
      const human = humanizeError(cause);
      toast.error(human.message, { code: human.code });
    },
  });

  const members = membersQuery.data ?? [];
  const adminsCount = members.filter((member) => member.role === 'project_admin').length;

  const candidateItems = useMemo(
    () =>
      (usersQuery.data?.items ?? [])
        .filter((candidate: User) => !members.some((member) => member.user.id === candidate.id))
        .map((candidate: User) => ({
          id: candidate.id,
          label: candidate.displayName,
          description: candidate.login,
        })),
    [members, usersQuery.data?.items],
  );

  const columns: readonly TableColumn<Member>[] = [
    {
      id: 'user',
      header: 'Пользователь',
      render: (member) => (
        <span className={styles['cellUser']}>
          <Avatar user={member.user} size="sm" />
          <span>
            <span>{member.user.displayName}</span>
            <br />
            <span className={cn('u-muted', 'u-mono')}>@{member.user.login}</span>
          </span>
          {member.user.id === user?.id ? <span className="u-muted">— это вы</span> : null}
        </span>
      ),
    },
    {
      id: 'role',
      header: 'Роль',
      width: '170px',
      render: (member) => (
        <Select
          options={ROLE_OPTIONS}
          value={member.role}
          disabled={member.role === 'project_admin' && adminsCount === 1}
          onChange={(role) => roleMutation.mutate({ userId: member.user.id, role })}
        />
      ),
    },
    {
      id: 'since',
      header: 'В проекте с',
      render: (member) => <span className="u-muted">{formatDate(member.createdAt)}</span>,
    },
    {
      id: 'actions',
      header: '',
      width: '48px',
      align: 'end',
      render: (member) => (
        <IconButton
          size="sm"
          tone="danger"
          label={`Удалить ${member.user.displayName} из проекта`}
          icon={<Trash2 size={14} />}
          onClick={() => {
            setReassign('keep');
            setMemberToRemove(member);
          }}
        />
      ),
    },
  ];

  if (membersQuery.isPending) {
    return <Skeleton variant="row" count={5} />;
  }

  return (
    <div className={styles['section']}>
      <div className={styles['sectionHeader']}>
        <h2>Участники ({members.length})</h2>
        <Button variant="primary" icon={<UserPlus size={14} />} onClick={() => setAddOpen(true)}>
          Добавить
        </Button>
      </div>

      {members.length === 0 ? (
        <EmptyState icon={<Users size={20} />} title="В проекте нет участников" />
      ) : (
        <Table
          ariaLabel="Участники"
          columns={columns}
          rows={members}
          getRowId={(member) => member.user.id}
        />
      )}

      <p className={styles['hint']}>
        Последнего администратора проекта удалить или понизить нельзя — сначала назначьте другого.
      </p>

      <Modal
        open={isAddOpen}
        onClose={() => setAddOpen(false)}
        title="Добавить участников"
        footer={
          <>
            <Button variant="ghost" onClick={() => setAddOpen(false)}>
              Отмена
            </Button>
            <Button
              variant="primary"
              loading={addMutation.isPending}
              disabled={selectedUserIds.length === 0}
              onClick={() => addMutation.mutate()}
            >
              Добавить
            </Button>
          </>
        }
      >
        <div className={styles['form']}>
          <Combobox
            label="Пользователи"
            multiple
            items={candidateItems}
            value={selectedUserIds}
            isLoading={usersQuery.isPending}
            ariaLabel="Пользователи для добавления"
            emptyText="Все активные пользователи уже в проекте"
            onChange={(value) => setSelectedUserIds(value)}
          />
          <Select label="Роль" options={ROLE_OPTIONS} value={newRole} onChange={(value) => setNewRole(value)} />
        </div>
      </Modal>

      <Modal
        open={memberToRemove !== null}
        onClose={() => setMemberToRemove(null)}
        title="Удалить участника?"
        size="sm"
        footer={
          <>
            <Button variant="ghost" onClick={() => setMemberToRemove(null)}>
              Отмена
            </Button>
            <Button variant="danger" loading={removeMutation.isPending} onClick={() => {
              if (memberToRemove) {
                removeMutation.mutate(memberToRemove.user.id);
              }
            }}>
              Удалить
            </Button>
          </>
        }
      >
        <div className={styles['form']}>
          <p>
            {memberToRemove?.user.displayName} потеряет доступ к проекту. Что сделать с назначенными задачами?
          </p>
          <label className={styles['row']}>
            <input type="radio" name="reassign" checked={reassign === 'keep'} onChange={() => setReassign('keep')} />
            Оставить на нём (задачи останутся назначенными)
          </label>
          <label className={styles['row']}>
            <input type="radio" name="reassign" checked={reassign === 'null'} onChange={() => setReassign('null')} />
            Снять назначение
          </label>
        </div>
      </Modal>
    </div>
  );
}
